import React, { useState, useEffect } from 'react';
import './courses.css'
import C2wCourseCard from '../../../common/C2wHome/courses/courseCard';
import { useNavigate } from 'react-router';
import { Helmet } from 'react-helmet';
import coursesData from "../../../../src/data/coursesData.json"
import { motion } from 'framer-motion';

function C2wCourses() {

    const navigate = useNavigate()

    /**
 * @state windowWidth - Tracks the current width of the browser window.
 * @hook useEffect - Window Resize Listener
 * @author Vishal Lohbande
 * Description:
 * - Initializes `windowWidth` state with the current width of the browser window.
 * - Adds an event listener to update the state whenever the window is resized.
 *
 * Behavior:
 * - `handleResize` updates the `windowWidth` state using `window.innerWidth`.
 * - The resize listener is attached once on component mount.
 * - On unmount, the event listener is properly cleaned up to prevent memory leaks.
 *
 * Use Case:
 * - Responsive UI adjustments (e.g., conditional rendering based on screen width).
 * - Dynamic layout shifts or breakpoints detection in React components.
 *
 * Notes:
 * - This setup ensures that UI can respond in real-time to screen size changes.
 */
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [activeTab, setActiveTab] = useState('Upcoming');

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Filter courses based on activeTab:
    // - activeTab === 'Upcoming' -> type === 'Upcoming'
    // - activeTab === 'Offline' -> type === 'Offline'
    // - activeTab === 'Recorded' -> type === 'Online'
    const filteredCourses = coursesData?.courses?.filter(obj => {
        if (activeTab === 'Upcoming') return obj?.type === 'Upcoming' || obj?.type === 'Fullstack';
        if (activeTab === 'Offline') return obj?.type === 'Offline';
        if (activeTab === 'Recorded') return obj?.type === 'Online';
        return true;
    }) || [];

    const renderedCoursesCards = filteredCourses.slice(0, 4).map((obj, index) => {
        return (
            <C2wCourseCard key={obj?.id || index} obj={obj} />
        );
    });

    return (
        windowWidth > 768 ?
            <div className='C2wCourses_ctn'>
                <Helmet>
                    <title>{`Courses`}</title>
                    <meta name="description" content="High-quality Course with best Content." />
                    <meta name="robots" content="index, follow" />
                </Helmet>
                <div className='C2wCoursesHeading_ctn_hld'>
                    <div className='C2wCoursessHeading_ctn'>
                        {'Explore our interactive '}
                        <span className='C2wCorsessHeadingSpan'>{'Courses'}</span>
                    </div>
                    <div className='C2wCourses_ctn_subheading'>{' Beginner friendly | From Scratch to advanced | Value for money | Learn from the best in industry'}</div>
                </div>

                <div className='C2wCourses_main_layout'>
                    {/* Left Sidebar Menu */}
                    <div className='C2wCourses_sidebar'>
                        <div className='C2wCourses_menu'>
                            <button 
                                className={`C2wCourses_menu_item ${activeTab === 'Upcoming' ? 'active' : ''}`}
                                onClick={() => setActiveTab('Upcoming')}
                            >
                                <span>Upcoming Courses</span>
                                {activeTab === 'Upcoming' && <span className='chevron-arrow'>&rsaquo;</span>}
                            </button>
                            <button 
                                className={`C2wCourses_menu_item ${activeTab === 'Offline' ? 'active' : ''}`}
                                onClick={() => setActiveTab('Offline')}
                            >
                                <span>Offline Courses</span>
                                {activeTab === 'Offline' && <span className='chevron-arrow'>&rsaquo;</span>}
                            </button>
                            <button 
                                className={`C2wCourses_menu_item ${activeTab === 'Recorded' ? 'active' : ''}`}
                                onClick={() => setActiveTab('Recorded')}
                            >
                                <span>Recorded Courses</span>
                                {activeTab === 'Recorded' && <span className='chevron-arrow'>&rsaquo;</span>}
                            </button>
                        </div>

                        {/* View All Courses Button inside Sidebar */}
                        <button className="C2wCoursesExploreBtn" onClick={() => {
                            navigate('/courses')
                        }}>
                            <span className="buttonText">View All Courses</span>
                            <div className="AnimatedButton">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 12H23" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 5L23 12L16 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <svg className="defaultArrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 12H23" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M16 5L23 12L16 19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>

                    {/* Vertical Divider */}
                    <div className='C2wCourses_divider'></div>

                    {/* Right Grid Content */}
                    <div className='C2wCorses_card_ctn'>
                        {renderedCoursesCards.length > 0 ? renderedCoursesCards : (
                            <div className="upcoming-course-placeholder">
                                <div className="placeholder-logos">
                                    <div className="tech-logo-wrapper react-logo">
                                        <img src="/assets/images/landing/reactLogoSvg.svg" alt="React" className="tech-logo" />
                                    </div>
                                    <div className="logos-plus">+</div>
                                    <div className="tech-logo-wrapper spring-logo">
                                        <img src="/assets/images/landing/springLogoSvg.svg" alt="Spring Boot" className="tech-logo" />
                                    </div>
                                </div>
                                <h3 className="placeholder-title"><span className="highlight-gradient">Full Stack</span></h3>
                                <p className="placeholder-desc">Our most awaited course is coming soon. Stay tuned!</p>
                            </div>
                        )}
                    </div>
                </div>
            </div> :
            <div className='C2wCourses_ctn'>
                <Helmet>
                    <title>{`Courses`}</title>
                    <meta name="description" content="High-quality Course with best Content." />
                    <meta name="robots" content="index, follow" />
                </Helmet>
                <div className='C2wCoursesHeading_ctn_hld'>
                    <div className='C2wCoursessHeading_ctn'>
                        {'Explore Popular '}
                        <span className='C2wCorsessHeadingSpan'>{'Courses'}</span>
                    </div>
                    <div className='C2wCourses_ctn_subheading'>{'Beginner friendly | From Scratch to advanced | Value for money | Learn from the best in industry'}</div>
                </div>

                {/* Mobile horizontal scrolling category selector */}
                <div className='C2wCourses_mobile_tabs'>
                    <button 
                        className={`C2wCourses_mobile_tab_btn ${activeTab === 'Upcoming' ? 'active' : ''}`}
                        onClick={() => setActiveTab('Upcoming')}
                    >
                        Upcoming
                    </button>
                    <button 
                        className={`C2wCourses_mobile_tab_btn ${activeTab === 'Offline' ? 'active' : ''}`}
                        onClick={() => setActiveTab('Offline')}
                    >
                        Offline
                    </button>
                    <button 
                        className={`C2wCourses_mobile_tab_btn ${activeTab === 'Recorded' ? 'active' : ''}`}
                        onClick={() => setActiveTab('Recorded')}
                    >
                        Recorded
                    </button>
                </div>

                <div className='C2wCorses_card_ctn'>
                    {renderedCoursesCards.length > 0 ? renderedCoursesCards : (
                        <div className="upcoming-course-placeholder">
                            <div className="placeholder-logos">
                                <div className="tech-logo-wrapper react-logo">
                                    <img src="/assets/images/landing/reactLogoSvg.svg" alt="React" className="tech-logo" />
                                </div>
                                <div className="logos-plus">+</div>
                                <div className="tech-logo-wrapper spring-logo">
                                    <img src="/assets/images/landing/springLogoSvg.svg" alt="Spring Boot" className="tech-logo" />
                                </div>
                            </div>
                            <h3 className="placeholder-title"><span className="highlight-gradient">Full Stack</span></h3>
                            <p className="placeholder-desc">Our most awaited course is coming soon. Stay tuned!</p>
                        </div>
                    )}
                </div>
                <button className="C2wCoursesExploreBtn coursesBtn" onClick={() => {
                    navigate('/courses')
                }}>
                    <span className="buttonText">View All Courses</span>
                    <div className="AnimatedButton">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 12H23" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 5L23 12L16 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <svg className="defaultArrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 12H23" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16 5L23 12L16 19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
    );
}

export default C2wCourses;
